import { useState } from 'react';
import { createPortal } from 'react-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { sweepInbox } from '@/api/inbox';
import { MassUnsubscribeModal } from './mass-unsubscribe-modal';
import { cn } from '@/lib/utils';
import { Sparkles, MailX, Filter, X, Loader2, CheckCircle2, Mail } from 'lucide-react';
import { useUi } from '@/components/ui-dialogs';

interface SweepModalProps {
  mailboxId?: string;
  mailboxEmail?: string;
  folder?: string;
  spamAction?: 'clean' | 'review' | 'spam';
  mailboxes?: { id: string; email: string; displayName?: string | null }[];
  onClose: () => void;
}

type Phase = 'menu' | 'filter-sweep-running' | 'filter-sweep-done' | 'unsubscribe';

export function SweepModal({ mailboxId, mailboxEmail, folder, spamAction, mailboxes, onClose }: SweepModalProps) {
  const [phase, setPhase] = useState<Phase>('menu');
  const [sweptCount, setSweptCount] = useState(0);
  const [selectedMailboxId, setSelectedMailboxId] = useState<string | undefined>(mailboxId);
  // Explicit scope for the unsubscribe sweep — defaults to the view you opened
  // the sweep from, but is user-changeable so you can sweep Maybe/Definitely
  // spam directly from anywhere (incl. unified inbox).
  const [scope, setScope] = useState<'inbox' | 'review' | 'spam'>(
    spamAction === 'review' ? 'review' : spamAction === 'spam' ? 'spam' : 'inbox'
  );
  const queryClient = useQueryClient();
  const ui = useUi();

  // Active mailbox: either passed in directly, or selected from the picker
  const activeMailboxId = mailboxId || selectedMailboxId;
  const activeMailbox = mailboxes?.find((m) => m.id === activeMailboxId);

  const sweepMutation = useMutation({
    mutationFn: () => sweepInbox(activeMailboxId, folder),
    onSuccess: (r) => {
      queryClient.invalidateQueries({ queryKey: ['unified-inbox'] });
      queryClient.invalidateQueries({ queryKey: ['messages'] });
      setSweptCount(r.swept);
      setPhase('filter-sweep-done');
    }
  });

  // Unsubscribe phase — render the MassUnsubscribeModal directly
  if (phase === 'unsubscribe' && activeMailboxId) {
    return (
      <MassUnsubscribeModal
        mailboxId={activeMailboxId}
        mailboxEmail={activeMailbox?.email || mailboxEmail || ''}
        folder={scope === 'inbox' ? folder : undefined}
        spamAction={scope === 'inbox' ? undefined : scope === 'review' ? 'review' : 'spam'}
        onClose={() => setPhase('menu')}
      />
    );
  }

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={(e) => { if (e.target === e.currentTarget && phase !== 'filter-sweep-running') onClose(); }}>
      <div className="w-full max-w-md rounded-xl border border-bmail-border bg-bmail-surface p-6 shadow-xl" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="mb-4 flex items-center justify-between">
          <h2 className="flex items-center gap-2 text-lg font-semibold text-bmail-text">
            {phase === 'filter-sweep-done' ? <CheckCircle2 size={20} className="text-green-500" /> : <Sparkles size={20} />}
            {phase === 'filter-sweep-done' ? 'Sweep Complete' : 'Sweep'}
          </h2>
          {phase !== 'filter-sweep-running' && (
            <button onClick={onClose} className="text-bmail-text-secondary hover:text-bmail-text">
              <X size={18} />
            </button>
          )}
        </div>

        {/* Filter sweep running */}
        {phase === 'filter-sweep-running' && (
          <div className="flex items-center gap-2 text-sm text-bmail-text-secondary">
            <Loader2 size={16} className="animate-spin" /> Sweeping {activeMailboxId ? activeMailbox?.email || '' : 'all mailboxes'} by filter rules…
          </div>
        )}

        {/* Filter sweep done */}
        {phase === 'filter-sweep-done' && (
          <div className="space-y-4">
            <div className="text-sm text-bmail-text">
              {sweptCount > 0
                ? `${sweptCount} message${sweptCount === 1 ? '' : 's'} moved to Spam.`
                : 'Nothing matched your filters — the inbox is already clean.'}
            </div>
            <div className="flex justify-end gap-2">
              <button onClick={() => setPhase('menu')} className="rounded-lg border border-bmail-border px-4 py-2 text-sm text-bmail-text hover:bg-bmail-surface-hover">
                Back
              </button>
              <button onClick={onClose} className="rounded-lg bg-bmail-goldfill px-4 py-2 text-sm font-semibold text-bmail-on-accent hover:bg-bmail-goldfill-hover">
                Done
              </button>
            </div>
          </div>
        )}

        {/* Menu */}
        {phase === 'menu' && (
          <>
            {/* Mailbox picker — only show when no mailboxId was passed in (unified inbox) */}
            {!mailboxId && mailboxes && mailboxes.length > 0 && (
              <div className="mb-4">
                <label className="mb-1.5 block text-sm font-medium text-bmail-text">
                  <Mail size={14} className="mr-1 inline" /> Mailbox to sweep
                </label>
                <select
                  value={selectedMailboxId || ''}
                  onChange={(e) => setSelectedMailboxId(e.target.value || undefined)}
                  className="w-full rounded-lg border border-bmail-border bg-bmail-surface px-3 py-2 text-sm text-bmail-text focus:outline-none focus:ring-2 focus:ring-bmail-gold"
                >
                  <option value="">All mailboxes (filter sweep only)</option>
                  {mailboxes.map((m) => (
                    <option key={m.id} value={m.id}>{m.displayName || m.email}</option>
                  ))}
                </select>
                <p className="mt-1.5 text-xs text-bmail-text-secondary">
                  {activeMailboxId
                    ? `Sweeping ${activeMailbox?.email || ''}${folder && scope === 'inbox' ? ` · ${folder}` : ''}`
                    : 'Filter sweep runs across all mailboxes. Pick a mailbox to enable unsubscribe sweep.'}
                </p>
              </div>
            )}

            {/* Explicit scope picker — which category to scan for unsubscribe
                emails. Makes Maybe/Definitely spam sweepable from anywhere. */}
            {activeMailboxId && (
              <div className="mb-4">
                <label className="mb-1.5 block text-sm font-medium text-bmail-text">
                  <MailX size={14} className="mr-1 inline" /> Unsubscribe scope
                </label>
                <div className="grid grid-cols-3 gap-1 rounded-lg border border-bmail-border p-1">
                  {([
                    ['inbox', 'Inbox'],
                    ['review', 'Maybe spam'],
                    ['spam', 'Definitely spam']
                  ] as const).map(([value, label]) => (
                    <button
                      key={value}
                      onClick={() => setScope(value)}
                      className={cn(
                        'rounded-md px-2 py-1.5 text-xs font-medium transition-colors',
                        scope === value
                          ? 'bg-bmail-goldfill text-bmail-on-accent'
                          : 'text-bmail-text-secondary hover:bg-bmail-surface-hover'
                      )}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className="space-y-2">
              {/* Filter sweep */}
              <button
                onClick={() => { setPhase('filter-sweep-running'); sweepMutation.mutate(); }}
                disabled={sweepMutation.isPending}
                className="flex w-full items-start gap-3 rounded-lg border border-bmail-border p-4 text-left transition-colors hover:bg-bmail-surface-hover disabled:opacity-50"
              >
                <Filter size={20} className="mt-0.5 shrink-0 text-bmail-gold" />
                <div className="min-w-0">
                  <div className="font-medium text-bmail-text">Sweep by filter rules</div>
                  <div className="mt-0.5 text-xs text-bmail-text-secondary">
                    Re-apply your spam filters to cached mail{activeMailboxId ? ` in ${activeMailbox?.email || ''}` : ' across all mailboxes'}. Messages matching a block filter get moved to Spam.
                  </div>
                </div>
              </button>

              {/* Unsubscribe sweep */}
              <button
                onClick={() => setPhase('unsubscribe')}
                disabled={!activeMailboxId}
                className="flex w-full items-start gap-3 rounded-lg border border-bmail-border p-4 text-left transition-colors hover:bg-bmail-surface-hover disabled:opacity-50"
              >
                <MailX size={20} className="mt-0.5 shrink-0 text-bmail-gold" />
                <div className="min-w-0">
                  <div className="font-medium text-bmail-text">Sweep for unsubscribe emails</div>
                  <div className="mt-0.5 text-xs text-bmail-text-secondary">
                    {!activeMailboxId
                      ? 'Pick a mailbox above to enable this option.'
                      : `Scan ${activeMailbox?.email || ''} — ${scope === 'inbox' ? 'inbox' : scope === 'review' ? 'Maybe spam' : 'Definitely spam'} — for emails with unsubscribe links and mass-unsubscribe from senders.`}
                  </div>
                </div>
              </button>
            </div>

            <div className="mt-6 flex justify-end">
              <button onClick={onClose} className="rounded-lg border border-bmail-border px-4 py-2 text-sm text-bmail-text hover:bg-bmail-surface-hover">
                Close
              </button>
            </div>
          </>
        )}
      </div>
    </div>,
    document.body
  );
}